import { useCallback, useEffect, useMemo, useState, type CSSProperties } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { applyPageSeo } from "../../lib/seo";
import { toBrandLabel } from "../../lib/brand-labels";
import { templateLibrary, type TemplateCategory } from "../../../email-templates";
import { TemplateCardPreview } from "../../components/TemplateCardPreview";

type CategoryFilter = TemplateCategory | "all";
type BrandFilter = string | "all";
type SortMode = "brand" | "name" | "category";

type BrandTheme = {
  label: string;
  accent: string;
  surface: string;
  surfaceAlt: string;
  ink: string;
  textOnAccent: string;
};

const categoryOptions: CategoryFilter[] = [
  "all",
  "onboarding",
  "transactional",
  "marketing",
  "alerts",
  "confirmation",
  "invoice",
  "security",
  "collaboration",
  "social",
  "newsletter",
  "retention",
  "product-update",
];

const brandThemes: Record<string, BrandTheme> = {
  acme: {
    label: "Acme",
    accent: "#4f46e5",
    surface: "#eef2ff",
    surfaceAlt: "#c7d2fe",
    ink: "#312e81",
    textOnAccent: "#ffffff",
  },
  stripe: {
    label: "Stripe",
    accent: "#635bff",
    surface: "#f3f1ff",
    surfaceAlt: "#d9d6ff",
    ink: "#312e81",
    textOnAccent: "#ffffff",
  },
  airbnb: {
    label: "Airbnb",
    accent: "#ff385c",
    surface: "#fff1f3",
    surfaceAlt: "#fecdd3",
    ink: "#9f1239",
    textOnAccent: "#ffffff",
  },
  amazon: {
    label: "Amazon",
    accent: "#ff9900",
    surface: "#fff7e8",
    surfaceAlt: "#fde68a",
    ink: "#7c2d12",
    textOnAccent: "#111827",
  },
  shopify: {
    label: "Shopify",
    accent: "#008060",
    surface: "#edf9f4",
    surfaceAlt: "#bbf7d0",
    ink: "#166534",
    textOnAccent: "#ffffff",
  },
  uber: {
    label: "Uber",
    accent: "#111111",
    surface: "#f5f5f5",
    surfaceAlt: "#d4d4d8",
    ink: "#18181b",
    textOnAccent: "#ffffff",
  },
  dropbox: {
    label: "Dropbox",
    accent: "#0061ff",
    surface: "#eef4ff",
    surfaceAlt: "#bfdbfe",
    ink: "#1d4ed8",
    textOnAccent: "#ffffff",
  },
  figma: {
    label: "Figma",
    accent: "#7b61ff",
    surface: "#f4f1ff",
    surfaceAlt: "#ddd6fe",
    ink: "#5b21b6",
    textOnAccent: "#ffffff",
  },
  discord: {
    label: "Discord",
    accent: "#5865f2",
    surface: "#eef0ff",
    surfaceAlt: "#c7d2fe",
    ink: "#3730a3",
    textOnAccent: "#ffffff",
  },
  twitter: {
    label: "Twitter / X",
    accent: "#1d9bf0",
    surface: "#eff8ff",
    surfaceAlt: "#bae6fd",
    ink: "#0c4a6e",
    textOnAccent: "#ffffff",
  },
  google: {
    label: "Google",
    accent: "#1a73e8",
    surface: "#eef4ff",
    surfaceAlt: "#bfdbfe",
    ink: "#1d4ed8",
    textOnAccent: "#ffffff",
  },
  apple: {
    label: "Apple",
    accent: "#0071e3",
    surface: "#eef6ff",
    surfaceAlt: "#bfdbfe",
    ink: "#1e3a8a",
    textOnAccent: "#ffffff",
  },
  duolingo: {
    label: "Duolingo",
    accent: "#58cc02",
    surface: "#f3fde5",
    surfaceAlt: "#d9f99d",
    ink: "#3f6212",
    textOnAccent: "#102a00",
  },
  vercel: {
    label: "Vercel",
    accent: "#111111",
    surface: "#f5f5f5",
    surfaceAlt: "#d4d4d8",
    ink: "#18181b",
    textOnAccent: "#ffffff",
  },
  github: {
    label: "GitHub",
    accent: "#24292f",
    surface: "#f6f8fa",
    surfaceAlt: "#d0d7de",
    ink: "#1f2328",
    textOnAccent: "#ffffff",
  },
  slack: {
    label: "Slack",
    accent: "#4a154b",
    surface: "#f7f0f7",
    surfaceAlt: "#e9d5ff",
    ink: "#581c87",
    textOnAccent: "#ffffff",
  },
  notion: {
    label: "Notion",
    accent: "#111111",
    surface: "#f6f6f5",
    surfaceAlt: "#e5e5e5",
    ink: "#262626",
    textOnAccent: "#ffffff",
  },
  linear: {
    label: "Linear",
    accent: "#5e6ad2",
    surface: "#eef1ff",
    surfaceAlt: "#c7d2fe",
    ink: "#3730a3",
    textOnAccent: "#ffffff",
  },
  spotify: {
    label: "Spotify",
    accent: "#1db954",
    surface: "#edfdf4",
    surfaceAlt: "#bbf7d0",
    ink: "#166534",
    textOnAccent: "#052e16",
  },
  netflix: {
    label: "Netflix",
    accent: "#e50914",
    surface: "#fff1f2",
    surfaceAlt: "#fecaca",
    ink: "#991b1b",
    textOnAccent: "#ffffff",
  },
};

function toCategoryLabel(category: CategoryFilter) {
  if (category === "all") {
    return "All";
  }

  return category.charAt(0).toUpperCase() + category.slice(1);
}

function getBrandTheme(brand: string) {
  return (
    brandThemes[brand] ?? {
      label: toBrandLabel(brand),
      accent: "#0284c7",
      surface: "#f0f9ff",
      surfaceAlt: "#bae6fd",
      ink: "#0c4a6e",
      textOnAccent: "#ffffff",
    }
  );
}

export function DiscoverTemplates() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [brandFilter, setBrandFilter] = useState<BrandFilter>("all");
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>(() => {
    const param = searchParams.get("category");
    if (param && categoryOptions.includes(param as CategoryFilter)) {
      return param as CategoryFilter;
    }
    return "all";
  });
  const [brandSearch, setBrandSearch] = useState("");
  const [templateSearch, setTemplateSearch] = useState("");
  const [sortMode, setSortMode] = useState<SortMode>("brand");
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  useEffect(() => {
    const cleanup = applyPageSeo({
      title: "Email Template Gallery",
      description:
        "Browse 148 email templates across 75 brands. Search, filter, export as HTML or React.",
      path: "/discover/templates",
      keywords: ["email templates", "react email templates", "email template gallery", "transactional email templates", "marketing email templates"],
    });

    // Add ItemList structured data for Google rich results
    const itemListJsonLd = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": "React Email Template Gallery",
      "description": "148 email templates across 75 brands. Filter, export, or open in the AI builder.",
      "numberOfItems": templateLibrary.length,
      "itemListElement": templateLibrary.slice(0, 50).map((template, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": template.title,
        "url": `https://reactemailbuilder.com/templates/${template.brand}/${template.slug}`,
      })),
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.textContent = JSON.stringify(itemListJsonLd);
    script.id = "itemlist-jsonld";
    document.head.appendChild(script);

    return () => {
      cleanup();
      const existing = document.getElementById("itemlist-jsonld");
      if (existing) existing.remove();
    };
  }, []);

  const templates = useMemo(
    () => {
      const sorted = [...templateLibrary].sort((left, right) => {
        if (sortMode === "name") {
          return left.title.localeCompare(right.title);
        }

        if (sortMode === "category") {
          if (left.category === right.category) {
            return left.title.localeCompare(right.title);
          }

          return left.category.localeCompare(right.category);
        }

        if (left.brand === right.brand) {
          return left.title.localeCompare(right.title);
        }

        return left.brand.localeCompare(right.brand);
      });

      return sorted;
    },
    [sortMode],
  );

  const brandOptions = useMemo(
    () => ["all", ...Array.from(new Set(templates.map((template) => template.brand)))],
    [templates],
  );

  const filteredTemplates = useMemo(
    () =>
      templates.filter((template) => {
        const matchesBrand =
          brandFilter === "all" || template.brand === brandFilter;
        const matchesCategory =
          categoryFilter === "all" || template.category === categoryFilter;

        const searchLower = templateSearch.trim().toLowerCase();
        const matchesSearch =
          !searchLower ||
          template.title.toLowerCase().includes(searchLower) ||
          template.brand.toLowerCase().includes(searchLower) ||
          template.description.toLowerCase().includes(searchLower) ||
          template.tags.some((tag) => tag.toLowerCase().includes(searchLower));

        return matchesBrand && matchesCategory && matchesSearch;
      }),
    [brandFilter, categoryFilter, templateSearch, templates],
  );

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = { all: templates.length };
    for (const t of templates) {
      counts[t.category] = (counts[t.category] ?? 0) + 1;
    }
    return counts;
  }, [templates]);

  const isFiltered = brandFilter !== "all" || categoryFilter !== "all" || templateSearch.trim() !== "";

  function resetFilters() {
    setBrandFilter("all");
    setCategoryFilter("all");
    setTemplateSearch("");
    setSearchParams({}, { replace: true });
  }

  const filterSidebar = (
    <div className="space-y-5">
      {/* Category */}
      <div>
        <div className="flex items-center justify-between">
          <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
            Category
          </label>
          <span className="text-xs text-slate-400">
            {filteredTemplates.length}/{templates.length}
          </span>
        </div>
        <div className="mt-2 flex flex-col gap-1">
          {categoryOptions.map((category) => {
            const isActive = categoryFilter === category;
            const count = categoryCounts[category] ?? 0;

            return (
              <button
                key={category}
                type="button"
                aria-pressed={isActive}
                onClick={() => {
                  setCategoryFilter(category);
                  setSearchParams(category === "all" ? {} : { category }, { replace: true });
                }}
                className={`flex items-center justify-between rounded px-3 py-1.5 text-xs font-medium transition ${
                  isActive
                    ? "bg-slate-950 text-white"
                    : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                <span>{toCategoryLabel(category)}</span>
                <span className={`tabular-nums ${isActive ? "text-slate-300" : "text-slate-400"}`}>{count}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Brand */}
      <div>
        <div className="flex items-center justify-between">
          <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
            Brand
          </label>
          {isFiltered && (
            <button
              type="button"
              onClick={resetFilters}
              className="text-xs font-medium text-sky-600 hover:text-sky-700"
            >
              Reset
            </button>
          )}
        </div>
        <div className="relative mt-2">
          <svg aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clipRule="evenodd" />
          </svg>
          <input
            type="text"
            placeholder="Filter brands..."
            value={brandSearch}
            onChange={(e) => setBrandSearch(e.target.value)}
            className="w-full rounded-md border border-slate-200 bg-white py-1.5 pl-9 pr-3 text-xs text-slate-700 placeholder:text-slate-400 focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-100"
          />
        </div>
        <div className="mt-2 max-h-64 space-y-0.5 overflow-y-auto">
          {brandOptions.filter((brand) => {
            if (!brandSearch.trim()) return true;
            if (brand === "all") return true;
            const theme = getBrandTheme(brand);
            return theme.label.toLowerCase().includes(brandSearch.toLowerCase());
          }).map((brand) => {
            const theme = getBrandTheme(brand);
            const isActive = brandFilter === brand;

            return (
              <button
                key={brand}
                type="button"
                aria-pressed={isActive}
                onClick={() => setBrandFilter(brand)}
                className={`flex w-full items-center gap-2 rounded px-2.5 py-1.5 text-xs font-medium transition ${
                  isActive
                    ? "text-white"
                    : "text-slate-600 hover:bg-slate-100"
                }`}
                style={isActive ? { background: theme.accent } : undefined}
              >
                <span
                  className="h-2 w-2 shrink-0 rounded-full"
                  style={{ backgroundColor: isActive ? "rgba(255,255,255,0.5)" : theme.accent }}
                />
                <span>{brand === "all" ? "All brands" : theme.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );

  return (
    <section className="relative min-h-screen bg-white">
      {/* Header bar */}
      <div className="app-surface relative overflow-hidden border-b border-slate-200/70">
        <div aria-hidden="true" className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-sky-300/70 to-transparent" />
        <div aria-hidden="true" className="absolute inset-0 bg-grid-fade opacity-50" />
        <div className="relative mx-auto flex max-w-7xl items-end justify-between px-4 py-7 sm:px-6 sm:py-9 lg:px-8">
          <div>
            <p className="eyebrow">Gallery</p>
            <h1 className="mt-2 text-2xl font-semibold tracking-tight text-slate-950 sm:text-3xl">
              Email templates
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              {filteredTemplates.length} of {templates.length} · 75 brands · all free to preview
            </p>
          </div>

          {/* Mobile filter toggle */}
          <button
            type="button"
            onClick={() => setMobileFiltersOpen(!mobileFiltersOpen)}
            className="flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 lg:hidden"
          >
            <svg aria-hidden="true" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M2 4.75A.75.75 0 012.75 4h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 4.75zm0 5A.75.75 0 012.75 9h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 9.75zm0 5a.75.75 0 01.75-.75h8.5a.75.75 0 010 1.5h-8.5a.75.75 0 01-.75-.75z" clipRule="evenodd" />
            </svg>
            Filters
            {isFiltered && (
              <span className="flex h-4 w-4 items-center justify-center rounded-full bg-sky-500 text-[10px] font-bold text-white">
                {(brandFilter !== "all" ? 1 : 0) + (categoryFilter !== "all" ? 1 : 0) + (templateSearch.trim() !== "" ? 1 : 0)}
              </span>
            )}
          </button>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex gap-8 py-8">
          {/* Left sidebar — desktop always visible, mobile toggle */}
          <aside className="hidden w-56 shrink-0 lg:block">
            <div className="sticky top-20 animate-sidebar-enter">
              {filterSidebar}
            </div>
          </aside>

          {/* Mobile filter drawer */}
          {mobileFiltersOpen && (
            <div className="fixed inset-0 z-50 lg:hidden">
              <div className="absolute inset-0 bg-slate-950/30" onClick={() => setMobileFiltersOpen(false)} />
              <div className="absolute inset-y-0 left-0 w-72 overflow-y-auto bg-white px-5 py-6 shadow-xl">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-slate-950">Filters</h2>
                  <button
                    type="button"
                    onClick={() => setMobileFiltersOpen(false)}
                    className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                    aria-label="Close filters"
                  >
                    <svg aria-hidden="true" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
                <div className="mt-4">
                  {filterSidebar}
                </div>
              </div>
            </div>
          )}

          {/* Main content — template grid */}
          <div className="min-w-0 flex-1">
            <div className="mb-4 rounded-lg border border-slate-200/70 bg-white/85 p-3 shadow-soft backdrop-blur">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div className="relative min-w-0 flex-1">
                  <svg aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clipRule="evenodd" />
                  </svg>
                  <input
                    type="text"
                    aria-label="Search templates"
                    placeholder="Search templates, brands, use cases..."
                    value={templateSearch}
                    onChange={(e) => setTemplateSearch(e.target.value)}
                    className="w-full rounded-md border border-slate-200 bg-slate-50 py-2 pl-9 pr-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-sky-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-100"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <label htmlFor="template-sort" className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                    Sort
                  </label>
                  <select
                    id="template-sort"
                    value={sortMode}
                    onChange={(event) => setSortMode(event.target.value as SortMode)}
                    className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-100"
                  >
                    <option value="brand">Brand</option>
                    <option value="name">Template name</option>
                    <option value="category">Category</option>
                  </select>
                </div>
              </div>

              {isFiltered ? (
                <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-3 text-xs text-slate-500">
                  <span>
                    Showing {filteredTemplates.length} of {templates.length} templates
                  </span>
                  <button
                    type="button"
                    onClick={resetFilters}
                    className="font-semibold text-sky-600 transition hover:text-sky-700"
                  >
                    Clear filters
                  </button>
                </div>
              ) : null}
            </div>

            {filteredTemplates.length > 0 ? (
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {filteredTemplates.map((template, index) => {
                  const theme = getBrandTheme(template.brand);

                  return (
                    <Link
                      key={template.id}
                      to={`/templates/${template.brand}/${template.slug}`}
                      className="group animate-card-enter overflow-hidden rounded-lg border border-slate-200/70 bg-white shadow-soft transition duration-200 hover:-translate-y-1 hover:border-slate-300 hover:shadow-card"
                      style={{
                        animationDelay: `${Math.min(index * 20, 400)}ms`,
                      }}
                    >
                      <div className="p-3 pb-0">
                        <TemplateCardPreview
                          brand={template.brand}
                          slug={template.slug}
                          accent={theme.accent}
                        />
                      </div>

                      <div className="px-3 pb-3">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span
                            className="rounded px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.18em]"
                            style={{
                              backgroundColor: `${theme.accent}12`,
                              color: theme.ink,
                            }}
                          >
                            {theme.label}
                          </span>
                          <span className="rounded bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                            {toCategoryLabel(template.category)}
                          </span>
                        </div>

                        <h3 className="mt-2 text-sm font-semibold tracking-tight text-slate-950 transition group-hover:text-sky-600">
                          {template.title}
                        </h3>

                        <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-2">
                          <span className="text-xs font-medium text-slate-400">
                            View
                          </span>
                          <span className="inline-flex h-6 w-6 items-center justify-center rounded bg-slate-950 text-white transition group-hover:translate-x-1">
                            <svg
                              className="h-3 w-3"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M5 12h14M13 5l7 7-7 7"
                              />
                            </svg>
                          </span>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            ) : (
              <div className="rounded-md border border-dashed border-slate-300 bg-white px-6 py-12 text-center">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                  No results
                </p>
                <h2 className="mt-3 text-xl font-semibold tracking-tight text-slate-950">
                  No matches found
                </h2>
                <p className="mx-auto mt-2 max-w-sm text-sm leading-6 text-slate-500">
                  Try different keywords or clear your filters.
                </p>
                {isFiltered ? (
                  <button
                    type="button"
                    onClick={resetFilters}
                    className="mt-6 rounded-md bg-slate-950 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800"
                  >
                    Reset filters
                  </button>
                ) : null}
              </div>
            )}

            {/* Conversion CTA — AI builder */}
            <div className="mt-8 overflow-hidden rounded-md bg-[linear-gradient(135deg,#0f172a_0%,#111827_52%,#0c4a6e_100%)] px-5 py-5 text-white shadow-[0_24px_70px_-35px_rgba(2,6,23,0.7)] sm:px-6">
              <div className="mx-auto flex max-w-5xl flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
                <div>
                  <h2 className="text-lg font-semibold tracking-tight sm:text-xl">
                    Need something custom?
                  </h2>
                  <p className="mt-0.5 text-xs text-slate-300">
                    Prompt → React Email → HTML
                  </p>
                </div>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <Link
                    to="/auth/signup?redirect=%2Fbuilder"
                    className="inline-flex items-center gap-2 rounded-md bg-white px-4 py-2 text-sm font-semibold text-slate-950 transition hover:bg-slate-100"
                  >
                    Try free
                    <svg aria-hidden="true" className="h-4 w-4" viewBox="0 0 20 20" fill="none">
                      <path d="M4.166 10h11.667m0 0-4.167-4.167M15.833 10l-4.167 4.167" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </Link>
                  <Link
                    to="/builder"
                    className="inline-flex items-center gap-2 rounded border border-white/35 bg-white/10 px-4 py-2.5 text-sm font-semibold text-white transition hover:border-white/60 hover:bg-white/15"
                  >
                    See demo
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
